import fp from 'lodash/fp';
import {
    takeEvery,
    all,
    put,
    call,
    select
} from 'redux-saga/effects';
import urlJoin from 'url-join';
import { push, replace, LOCATION_CHANGE } from 'connected-react-router';
import moment from 'moment';

import actionFactory from '../../webcommon/header/filterheader/actions';
import * as filterHeaderSelectors from '../../webcommon/header/filterheader/selectors';
import { getTimeStamp } from '../../webcommon/common/helpers';
import { dateTransferFormat } from '../../webcommon/common/constants';

import {
    searchToFilters, filtersToSearch, urlToSort, encodeSortingIntoUrl
} from '../../common/urlUtils';
import * as routerSelectors from '../../common/router/selectors';
import { getVirtualDirectory } from '../../common/helpermethods/HelperMethods';
import * as appSelectors from '../../app/selectors';
import { restructureListByType } from '../../api/adapters/filterAdapters';
import * as systemInfoApi from '../../api/systemInfoApi';
import * as appConstants from '../../common/constants';
import * as appAdapters from '../../api/adapters/systemInfoAdapter';
import * as appActions from '../../app/actions';

import * as constants from './constants';
import * as api from '../../api/impactchartApi';
import * as actions from './actions';
import * as selectors from './selectors';

const filterHeaderId = 'IM_MONITORING';

const filterHeaderActions = actionFactory(filterHeaderId);

export const PAGE_ROUTE = urlJoin(getVirtualDirectory(), '/summary/impactchart');

const testLocation = action => action.type === LOCATION_CHANGE
    && action.payload.location.pathname.toLowerCase() === PAGE_ROUTE.toLowerCase();

export function* fetchAvailableListValues() {
    const counterpartyList = yield select(appSelectors.getImCounterparties);
    const restructuredCounterparties = restructureListByType(counterpartyList);
    yield put(filterHeaderActions.setAvailableListValues('counterparties', restructuredCounterparties));
}

export function* initialised() {
    const initialised = yield select(selectors.getInitialised);

    if (!initialised) {
        yield put(filterHeaderActions.setFilterDefinitions(constants.filterDefinitions));
        const { defaultFilters } = constants;
        const availableEOD = yield select(appSelectors.getAvailableEOD);
        const dateFrom = fp.get('from')(availableEOD);
        const fromDate = moment(dateFrom).subtract(2, 'months').format(dateTransferFormat);
        const dateRange = fp.set('from', fromDate)(availableEOD);
        const filters = fp.set('[0].value', dateRange)(defaultFilters);
        const startDate = moment(dateRange.from).format(appConstants.dateFormat);
        const endDate = moment(dateRange.to).format(appConstants.dateFormat);
        const csas = yield call(systemInfoApi.csasByEodRange, { RunStartDate: startDate, RunEndDate: endDate });
        const csaList = appAdapters.createCsaList(csas);
        yield put(appActions.fetchCsaDataSucceeded(csaList));
        if (!csaList.length) {
            yield put(appActions.fetchCsaDataEmpty('The CSA list was empty for the chosen date'));
        }
        yield put(filterHeaderActions.setDefaultFilters(filters));
        yield call(fetchAvailableListValues);
        yield put(actions.setInitialised());
        if (!window.location.search) {
            yield put(filterHeaderActions.resetFilters());
            yield put(actions.fetchImMonitoringRequest());
        } else {
            yield put(replace(`${PAGE_ROUTE}${window.location.search}`));
        }
    }
}

export function* handleFilterChanged() {
    yield put(actions.fetchImMonitoringRequest());
}

export function* fetchImMonitoring(api) {
    try {
        const filters = yield select(filterHeaderSelectors.getFilters, filterHeaderId);
        const results = yield call(api.fetchImMonitoring, filters);
        const timestamp = yield call(getTimeStamp);
        yield put(actions.fetchImMonitoringSuccess(results, timestamp));
    } catch (error) {
        yield put(actions.fetchImMonitoringError(error.toString()));
    }
}

export function* filterChanged(action) {
    const initialised = yield select(selectors.getInitialised);
    if (initialised) {
        if (action.filterId === 'date') {
            try {
                const EodDateStart = moment(action.value.from).format(appConstants.dateFormat);
                const EodDateEnd = moment(action.value.to).format(appConstants.dateFormat);
                const csas = yield call(systemInfoApi.csasByEodRange, { RunStartDate: EodDateStart, RunEndDate: EodDateEnd });
                const csaList = appAdapters.createCsaList(csas);
                yield put(appActions.fetchCsaDataSucceeded(csaList));
                if (!csaList.length) {
                    yield put(appActions.fetchCsaDataEmpty('The CSA list was empty for the chosen date'));
                }
                yield call(fetchAvailableListValues);
            } catch (error) {
                yield put(appActions.fetchCsaDataFailed(error.message));
            }
        }
        if (action.type === filterHeaderActions.RESET_FILTERS) {
            const filters = yield select(filterHeaderSelectors.getFilters, filterHeaderId);
            const EodDateStart = moment(filters[0].value.from).format(appConstants.dateFormat);
            const EodDateEnd = moment(filters[0].value.to).format(appConstants.dateFormat);
            const csas = yield call(systemInfoApi.csasByEodRange, { RunStartDate: EodDateStart, RunEndDate: EodDateEnd });
            const csaList = appAdapters.createCsaList(csas);
            yield put(appActions.fetchCsaDataSucceeded(csaList));
            if (!csaList.length) {
                yield put(appActions.fetchCsaDataEmpty('The CSA list was empty for the chosen date'));
            }
            yield call(fetchAvailableListValues);
        }
        const filters = yield select(filterHeaderSelectors.getFilters, filterHeaderId);
        const sorting = yield select(selectors.getSorting);
        const filterSearch = filtersToSearch(filters);
        const sortSearch = encodeSortingIntoUrl(sorting);
        const newSearch = [filterSearch, sortSearch].filter(fp.identity).join('&');

        const currentSearch = yield select(routerSelectors.getSearch);
        if (!currentSearch || currentSearch !== newSearch) {
            yield put(push(`${PAGE_ROUTE}?${newSearch}`));
        }
    }
}

export function* handleLocationChanged(action) {
    const initialised = yield select(selectors.getInitialised);

    if (initialised) {
        if (!action.payload.location.search) {
            yield call(filterChanged);
        } else {
            const { filterDefinitions } = constants;
            const { defaultFilters } = constants;
            const currentFilters = yield select(filterHeaderSelectors.getFilters, filterHeaderId);
            const currentSorts = yield select(selectors.getSorting);

            const queryFilters = searchToFilters(
                action.payload.location.search,
                filterDefinitions,
                defaultFilters
            );

            const querySorts = urlToSort(action.payload.location.search);

            const shouldUpdateFilters = !fp.isEqual(currentFilters)(queryFilters);
            const shouldUpdateSorts = !fp.isEqual(currentSorts)(querySorts);

            if (shouldUpdateFilters) { yield put(filterHeaderActions.setFilters(queryFilters)); }
            if (shouldUpdateSorts) { yield put(actions.setSorting(querySorts)); }

            if (!shouldUpdateSorts) { yield put(actions.refreshData()); }
        }
    }
}

export function* refreshCsaChart(api){
    try{
        const filters = yield select(filterHeaderSelectors.getFilters, filterHeaderId);
        const requestedServerFilters = fp.flow(
            fp.filter(filter => filter.value && filter.definition.server),
            fp.sortBy(filter => filter.definition.id),
            JSON.stringify
        )(filters);
        const results = yield call(api.fetchImMonitoring, requestedServerFilters);
        const timestamp = yield call(getTimeStamp);
        yield put(actions.fetchImMonitoringSuccess(results, timestamp));}
    catch (error){
        yield put(actions.fetchImMonitoringError(error.toString()));
    }
    
}

export default function* rootSaga() {
    yield all([
        takeEvery(
            actions.INITIALISED,
            initialised
        ),
        takeEvery(
            [
                actions.FETCH_IM_MONITORING_REQUEST,
                actions.REFRESH_DATA
            ],
            fetchImMonitoring,
            api
        ),
        takeEvery(
            [
                filterHeaderActions.UPDATE_FILTER,
                filterHeaderActions.REMOVE_FILTER,
                filterHeaderActions.RESET_FILTERS,
                actions.SET_SORTING
            ],
            filterChanged
        ),
        takeEvery(
            [
                filterHeaderActions.FETCH_AVAILABLE_LIST_VALUES,
                filterHeaderActions.OPEN_FILTER
            ],
            fetchAvailableListValues
        ),
        takeEvery(
            testLocation,
            handleLocationChanged
        ),
        takeEvery(actions.REFRESH_CSACHART, refreshCsaChart, api)
    ]);
}
